import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { CONCEPT_TERRITORIES } from "@/lib/pulseConstants";
import { Button } from "@/components/ui/button";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

const HOMEPAGE_FEATURED_KEYS = ["kopitiam-after-dark", "product-playground", "creator-house"];

/**
 * Mobile Creative Territories carousel.
 *
 * Presents the three featured homepage territories as a horizontally
 * swipeable, CSS scroll-snap carousel with a small peek of the next card.
 * Preserves tags, titles and a single concise sentence per card.
 *
 * The "Explore all creative territories" CTA continues to link to /work,
 * where all six territories are available in full.
 */
export default function MobileConceptTerritoriesCarousel() {
    const featured = HOMEPAGE_FEATURED_KEYS
        .map((k) => CONCEPT_TERRITORIES.find((c) => c.key === k))
        .filter(Boolean);

    const scrollerRef = useRef(null);
    const slideRefs = useRef([]);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                        const idx = Number(entry.target.getAttribute("data-slide-index"));
                        if (!Number.isNaN(idx)) setCurrent(idx);
                    }
                });
            },
            { root: scroller, threshold: [0.6, 0.9] }
        );
        slideRefs.current.forEach((el) => { if (el) io.observe(el); });
        return () => io.disconnect();
    }, [featured.length]);

    function scrollTo(index) {
        const el = slideRefs.current[index];
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
        }
    }

    return (
        <section
            className="section-pad relative border-t border-white/10"
            data-testid="home-concept-territories-mobile-section"
        >
            <div className="container-editorial">
                <div className="mb-8">
                    <SectionEyebrow index="06" label="Creative Territories" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Where a brief
                            <br />
                            could <span className="text-[#FF2A2A]">take us.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="body-md mt-4">
                            Starting points for brands that want more than a conventional event format. Concept directions&mdash;not delivered client work.
                        </p>
                    </Reveal>
                </div>
            </div>

            <div
                ref={scrollerRef}
                role="region"
                aria-roledescription="carousel"
                aria-label="Creative Territories"
                data-testid="concept-territories-mobile-scroller"
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 px-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                style={{ scrollPaddingLeft: "1.25rem" }}
            >
                {featured.map((c, i) => (
                    <article
                        key={c.key}
                        ref={(el) => { slideRefs.current[i] = el; }}
                        data-slide-index={i}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`${c.title} — ${i + 1} of ${featured.length}`}
                        data-testid={`concept-territory-mobile-card-${c.key}`}
                        className="snap-start shrink-0 w-[86%] max-w-[380px] rounded-2xl border border-white/10 bg-[#0B0C10] overflow-hidden"
                    >
                        <div className="relative w-full" style={{ aspectRatio: "4 / 3" }}>
                            <img
                                src={c.image}
                                alt={c.title}
                                loading="lazy"
                                decoding="async"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute top-3 left-3 z-10">
                                <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/15 text-[10px] font-mono uppercase tracking-widest text-white/85">
                                    {c.unlocks}
                                </span>
                            </div>
                            <div className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/60 backdrop-blur border border-white/15 flex items-center justify-center">
                                <ArrowUpRight size={14} className="text-white" />
                            </div>
                        </div>
                        <div className="p-5">
                            <div className="font-mono text-[11px] uppercase tracking-widest text-white/50 mb-2">
                                Concept / 0{i + 1}
                            </div>
                            <h3 className="font-display text-2xl text-white leading-tight">
                                {c.title}
                            </h3>
                            <p className="text-sm text-white/70 leading-relaxed mt-3">
                                {c.blurb}
                            </p>
                        </div>
                    </article>
                ))}
            </div>

            {/* Pagination */}
            <div className="container-editorial mt-5">
                <div
                    className="flex items-center justify-between gap-4"
                    data-testid="concept-territories-mobile-pagination"
                >
                    <div
                        className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60"
                        data-testid="concept-territories-mobile-counter"
                        aria-live="polite"
                    >
                        <span className="text-white">{String(current + 1).padStart(2, "0")}</span>
                        <span className="mx-2 text-white/30">/</span>
                        <span>{String(featured.length).padStart(2, "0")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {featured.map((c, i) => (
                            <button
                                key={c.key}
                                type="button"
                                aria-label={`Go to ${c.title}`}
                                aria-current={i === current ? "true" : undefined}
                                onClick={() => scrollTo(i)}
                                data-testid={`concept-territories-mobile-dot-${i}`}
                                className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${i === current ? "w-6 bg-[#FF2A2A]" : "w-2.5 bg-white/25 hover:bg-white/50"}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="container-editorial mt-8 flex justify-center">
                <Button
                    asChild
                    variant="outline"
                    className="rounded-full border-white/20 bg-transparent hover:bg-white/5 text-white h-12 px-6"
                >
                    <Link
                        to="/work"
                        data-testid="home-view-all-concepts-mobile"
                        className="inline-flex items-center gap-2"
                    >
                        Explore all creative territories <ArrowUpRight size={16} />
                    </Link>
                </Button>
            </div>

            {/* Compact conversion block — mobile-adapted */}
            <div className="container-editorial mt-8">
                <Reveal>
                    <div
                        data-testid="home-concept-territories-mobile-conversion"
                        className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0B0C10] via-[#0B0C10] to-[#170608] p-6 relative overflow-hidden"
                    >
                        <div className="absolute -top-16 -right-12 h-48 w-48 rounded-full bg-[#FF2A2A]/10 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="relative">
                            <h3 className="font-display text-2xl text-white leading-tight">
                                Have a brief in <span className="text-[#FF2A2A]">mind?</span>
                            </h3>
                            <p className="mt-2 body-md text-white/75">
                                Let&rsquo;s shape the territory together.
                            </p>
                            <Button
                                asChild
                                className="cta-glow mt-5 bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12 px-6 text-sm w-full sm:w-auto"
                                data-testid="concept-territories-mobile-conversion-cta"
                            >
                                <Link
                                    to="/contact"
                                    onClick={handlePulseBriefClick}
                                    className="inline-flex items-center justify-center gap-2"
                                >
                                    Start Your Pulse Brief <ArrowUpRight size={16} />
                                </Link>
                            </Button>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
